import { ActionIcon, Badge, Box, Button, Card, Code, Group, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCheck, IconEdit, IconShare, IconTrash, IconX } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { OptInUsersSearchRepository } from "shared/components/organization/users/data";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useAside } from "shared/utils/aside";
import { to } from "shared/utils/fns";
import { maybe } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { OptInUsersSearchRepositoryQuery } from "src/gql/graphql";
import { queryClient } from "src/queryClient";
import { container } from "tsyringe";

const DashCard = (props: { title: string; label: string }) => (
    <Card style={{ border: `.1rem solid`, borderRadius: 10 }} w="20%" miw={100}>
        <Stack>
            <Title fz={72} ta="center">
                {props.title}
            </Title>
            <Text ta="center">{props.label}</Text>
        </Stack>
    </Card>
);

const EditPanel = ({ user }: { user: OptInUsersSearchRepositoryQuery["optInUsers"][number] }) => {
    const aside = useAside();
    const repo = container.resolve(OptInUsersSearchRepository);

    const archiveUser = useGraphqlMutation(
        graphql(`
            mutation OptInArchiveUser($userId: UUID!) {
                archiveUser(userId: $userId)
            }
        `),
    );
    const removeEud = useGraphqlMutation(
        graphql(`
            mutation OptInRemoveEud($eudId: UUID!, $userId: UUID!) {
                removeEUD(eudId: $eudId, userId: $userId)
            }
        `),
    );

    return (
        <Stack maw="100%">
            <Text>
                User: <Code>{user.name}</Code>
            </Text>
            <Title order={3}>CMs</Title>
            <Table withRowBorders maw="100%">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Td>EUD</Table.Td>
                        <Table.Td>Verified?</Table.Td>
                        <Table.Td>Remove</Table.Td>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {user.euds.length == 0 && (
                        <Table.Tr>
                            <Table.Td colSpan={3} ta="center">
                                None
                            </Table.Td>
                        </Table.Tr>
                    )}
                    {user.euds.map((eud) => (
                        <Table.Tr key={eud.value + user.id}>
                            <Table.Td maw={0} flex={1} style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
                                {eud.value}
                            </Table.Td>
                            <Table.Td w={75}>
                                <Box w="fit-content" m="auto">
                                    {eud.isVerified ? <IconCheck size={16} color="green" /> : <IconX size={16} color="red" />}
                                </Box>
                            </Table.Td>
                            <Table.Td w={75}>
                                <ActionIcon
                                    onClick={() => {
                                        const newUser = {
                                            ...user,
                                            name: user.name ?? undefined,
                                            expiration: user.expiration ?? undefined,
                                            euds: user.euds.filter((e) => e.id != eud.id),
                                        } satisfies OptInUsersSearchRepositoryQuery["optInUsers"][number];

                                        void removeEud
                                            .mutateAsync({ userId: user.id, eudId: eud.id })
                                            .then(() => repo.upsert(newUser))
                                            .then(() => queryClient.invalidateQueries({ queryKey: ["organization-opt-in-metrics"] }))
                                            .then(() => notify.show.success("Removed EUD"))
                                            .then(() => aside.present({ label: "User Details", component: <EditPanel user={newUser} /> }))
                                            .catch(notify.catch);
                                    }}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
            <Button
                onClick={() => {
                    async function handleArchive() {
                        await notify.confirm("Are you sure you want to archive this user?", { title: "Confirm" });
                        await archiveUser
                            .mutateAsync({ userId: user.id })
                            .then(() => repo.remove(user.id))
                            .then(() => queryClient.invalidateQueries({ queryKey: ["organization-opt-in-metrics"] }))
                            .then(() => notify.show.success("Archived user"))
                            .catch(notify.catch);
                        modals.closeAll();
                        aside.dismiss();
                    }
                    handleArchive().catch(notify.catch);
                }}
            >
                Archive/Expire User
            </Button>
        </Stack>
    );
};

const Page = () => {
    const aside = useAside();
    const [emailSearch, setEmailSearch] = useDebouncedState("", 200);
    const [nameSearch, setNameSearch] = useDebouncedState("", 200);
    const [smsSearch, setSmsSearch] = useDebouncedState("", 200);

    const {
        data: { optInUserMetrics },
    } = useSuspenseQuery({
        queryKey: ["organization-opt-in-metrics"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query SupportOptInMetrics {
                        optInUserMetrics {
                            activeEmailCount
                            activeOptInUsersCount
                            activePhoneNumberCount
                        }
                    }
                `),
            ),
    });

    const loader = container.resolve(OptInUsersSearchRepository).get({
        filters: {
            email: maybe(emailSearch).takeIf((it) => it.length > 1),
            phoneNumber: maybe(smsSearch).takeIf((it) => it.length > 1),
            name: maybe(nameSearch).takeIf((it) => it.length > 1),
        },
    });
    const users = loader.useItems();

    return (
        <Content paper miw={365}>
            <Content.Heading>Opt-In Users</Content.Heading>
            <Group justify="space-around" wrap="nowrap">
                <DashCard title={optInUserMetrics.activePhoneNumberCount.toString()} label={"Phone Numbers"} />
                <DashCard title={optInUserMetrics.activeEmailCount.toString()} label={"Emails"} />
                <DashCard title={optInUserMetrics.activeOptInUsersCount.toString()} label={"Active Users"} />
            </Group>
            <Group justify="flex-end" align="flex-end">
                <Link to="/organization/opt-in-share">
                    <Button leftSection={<IconShare />} variant="default">
                        Share
                    </Button>
                </Link>
                <TextInput label="Search Name" onChange={(e) => setNameSearch(e.target.value)} />
                <TextInput label="Search Emails" onChange={(e) => setEmailSearch(e.target.value)} />
                <TextInput label="Search SMS #s" onChange={(e) => setSmsSearch(e.target.value)} />
            </Group>
            <Table withRowBorders>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Td>Name</Table.Td>
                        <Table.Th>Contact Method(s)</Table.Th>
                        <Table.Th>Signed Up</Table.Th>
                        <Table.Th>Expires</Table.Th>
                        <Table.Th />
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {(users?.length ?? 0) == 0 && (
                        <Table.Tr>
                            <Table.Td colSpan={4} ta="center">
                                No users
                            </Table.Td>
                        </Table.Tr>
                    )}
                    {users?.map((user) => (
                        <Table.Tr key={user.id}>
                            <Table.Td>{user.name ?? ""}</Table.Td>
                            <Table.Td>
                                {user.euds.map(to("value")).map((eud) => (
                                    <Badge key={eud + user.id} variant="default" mr="xs">
                                        {eud}
                                    </Badge>
                                ))}
                            </Table.Td>
                            <Table.Td>{new Date(parseInt(user.statusSince)).toLocaleString()}</Table.Td>
                            <Table.Td>{maybe(user.expiration)?.take((it) => new Date(parseInt(it)).toLocaleString()) ?? "Never"}</Table.Td>
                            <Table.Td>
                                <ActionIcon
                                    variant="transparent"
                                    onClick={() => aside.present({ label: "User Details", component: <EditPanel user={user} /> })}
                                >
                                    <IconEdit size={16} />
                                </ActionIcon>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
            <loader.LoadTrigger />
        </Content>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/organization/opt-in")({
    component: Page,
});
